import { ImageCropModal, ProfileImageUpload } from "@ews-admin/global-design-system";
import {
  deleteObject,
  getDownloadURL,
  getMetadata,
  listAll,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

import { DEFAULT_MANAGER_IMAGE_FEMALE, DEFAULT_MANAGER_IMAGE_MALE } from "@/types/manager.definition";
import { DEFAULT_SECRETARY_IMAGE_FEMALE, DEFAULT_SECRETARY_IMAGE_MALE } from "@/types/secretary.definition";
import { CivilityEnum, RoleEnum } from "@/types/user.definition";
import { storage } from "../../../firebase";
import useFacilityUser from "../../../hooks/useFacilityUser";
import { userApi } from "../../../services/user.api";
import { getSessionRequest } from "../../../store/reducers/sessionSlice";
import { envConfig } from "../../../utils/config-utils";

interface ProfileImageUploadWrapperProps {
  imageUrl: string;
  altText: string;
  readOnly?: boolean;
  size?: "sm" | "md" | "lg";
}

const ProfileImageUploadWrapper: React.FC<ProfileImageUploadWrapperProps> = ({
  imageUrl,
  altText,
  readOnly = false,
  size = "lg",
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const facilityUser = useFacilityUser();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [pendingFileName, setPendingFileName] = useState<string>("");

  const env = envConfig.env;
  const profileImagePath = `medecine360/${env}/profilePhotos/`;


  let defaultImage = DEFAULT_MANAGER_IMAGE_MALE;

  if (facilityUser?.roleName === RoleEnum.MANAGER) {
    defaultImage =
      facilityUser?.civility === CivilityEnum.MR
        ? DEFAULT_MANAGER_IMAGE_MALE
        : DEFAULT_MANAGER_IMAGE_FEMALE;
  }

  if (facilityUser?.roleName === RoleEnum.SECRETARY) {
    defaultImage =
      facilityUser?.civility === CivilityEnum.MR
        ? DEFAULT_SECRETARY_IMAGE_MALE
        : DEFAULT_SECRETARY_IMAGE_FEMALE;
  }


  const isDefault =
    !imageUrl || imageUrl === "" || imageUrl === defaultImage;

  const uploadFile = useCallback(
    async (file: File) => {
      if (!facilityUser?.id) return;
      const userId = String(facilityUser.userId);

      const fileExtension = file.name.substring(file.name.lastIndexOf(".")) || ".jpg";
      const userFile = `user_${userId}${fileExtension}`;
      const userFileWithoutExtension = `user_${userId}`;

      setIsUploading(true);

      try {
        const listResult = await listAll(ref(storage, profileImagePath));
        const existing = listResult.items.find((item) =>
          item.name.startsWith(userFileWithoutExtension),
        );
        if (existing) await deleteObject(existing);

        const storageRef = ref(storage, `${profileImagePath}${userFile}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            setUploadProgress(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
            );
          },
          (error) => {
            console.error("Upload error:", error);
            setIsUploading(false);
            setUploadProgress(0);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            await userApi.updateProfileImage(userId, { photoUrl: downloadURL });
            dispatch(getSessionRequest());
            setIsUploading(false);
          },
        );
      } catch (error) {
        console.error("Upload/delete error:", error);
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [facilityUser, profileImagePath, dispatch],
  );

  const handleFileSelect = useCallback((file: File) => {
    setCropSrc(URL.createObjectURL(file));
    setPendingFileName(file.name);
  }, []);

  const handleCropComplete = useCallback(
    (croppedFile: File) => {
      setCropSrc(null);
      uploadFile(croppedFile);
    },
    [uploadFile],
  );

  const handleCropClose = useCallback(() => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  }, [cropSrc]);

  const handleDeleteConfirm = async () => {
    if (!facilityUser?.photoUrl) return;

    const storageRef = ref(storage, facilityUser.photoUrl);
    try {
      await getMetadata(storageRef);
      await deleteObject(storageRef);
    } catch (error: unknown) {
      const fbError = error as { code?: string };
      if (fbError.code !== "storage/object-not-found") {
        console.error("Error deleting image:", error);
        return;
      }
    }

    const userId = String(facilityUser.id);
    await userApi.updateProfileImage(userId, { photoUrl: "" });
    toast.success(t("profile.image.deleted"));
    dispatch(getSessionRequest());
  };

  const handleFileSizeExceeded = useCallback(() => {
    toast.warning(t("profile.image.sizeExceeded", { max: 3 }));
  }, [t]);

  return (
    <>
      <ProfileImageUpload
        imageUrl={imageUrl || defaultImage}
        altText={altText}
        readOnly={readOnly}
        size={size}
        isLoading={isUploading}
        uploadProgress={uploadProgress}
        showDeleteButton={!isDefault}
        onFileSelect={handleFileSelect}
        onFileSizeExceeded={handleFileSizeExceeded}
        onDeleteConfirm={handleDeleteConfirm}
        deleteConfirmTitle={t("profile.image.delete")}
        deleteConfirmMessage={t("profile.image.confirmDelete")}
        deleteConfirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
      />

      {cropSrc && (
        <ImageCropModal
          isOpen
          imageSrc={cropSrc}
          originalFileName={pendingFileName}
          aspectRatio={1}
          onClose={handleCropClose}
          onCropComplete={handleCropComplete}
          title={t("cropModal.title")}
          confirmLabel={t("cropModal.confirm")}
          cancelLabel={t("common.cancel")}
          zoomLabel={t("cropModal.zoom")}
        />
      )}
    </>
  );
};

export default ProfileImageUploadWrapper;
